import { useEffect, useState } from 'react';

/**
 * Invitation context for the CANONICAL auth pages.
 *
 * An invited user is sent to `/auth/login` or `/auth/signup` — the same pages
 * everybody else uses — with the invitation carried in the existing `continue`
 * parameter. These helpers let those pages say WHY the user is there and
 * prefill the address the invitation is bound to, without either page growing
 * an authentication path of its own.
 *
 * The address is never read out of the URL: only the opaque invite token
 * travels there, and the address comes back from the server's own validation
 * of that token. Editing the query string therefore cannot change who the
 * invitation is for — and the acceptance endpoint re-derives everything from
 * the token again anyway.
 */

/** The Central route an invitation is accepted on. */
export const ACCEPT_INVITE_PATH = '/auth/accept-invite';

/** Build the continuation that returns the user to their invitation. */
export function buildInviteContinueUrl(token: string): string {
  return `${ACCEPT_INVITE_PATH}?token=${encodeURIComponent(token)}`;
}

/**
 * The invite token inside a `continue` value, or null when the continuation is
 * an ordinary one (an OIDC authorize URL, `/start/…`, …).
 *
 * Deliberately strict: only a same-origin, path-relative continuation that
 * points at the invite route is recognised, so this can never be steered at
 * another origin.
 */
export function inviteTokenFromContinueUrl(continueUrl: string | null | undefined): string | null {
  if (!continueUrl || !continueUrl.startsWith(ACCEPT_INVITE_PATH)) return null;
  const query = continueUrl.slice(continueUrl.indexOf('?') + 1);
  if (query === continueUrl) return null;
  const token = new URLSearchParams(query).get('token');
  return token && token.trim() ? token : null;
}

export interface InviteContinuationContext {
  /** Workspace the invitation is for. */
  workspaceName: string;
  /** The invited address, server-derived from the token. */
  email: string;
  /** Who sent it — masked server-side; the recipient has no claim to it. */
  inviterName: string;
  /** The role the invitation grants. Display only: the server binds it. */
  role: string;
  /** ISO timestamp, or '' when the server did not send one. */
  expiresAt: string;
}

/**
 * Resolve invitation context for a canonical auth page, or null when this is
 * not an invitation flow / the invitation is no longer valid.
 *
 * Fails quiet by design: if validation fails for any reason the page renders
 * exactly as it does for an ordinary visitor. The invitation is re-validated
 * server-side at acceptance, so nothing here is load-bearing for security.
 */
export function useInviteContinuation(continueUrl: string | null | undefined): InviteContinuationContext | null {
  const [context, setContext] = useState<InviteContinuationContext | null>(null);
  const token = inviteTokenFromContinueUrl(continueUrl);

  useEffect(() => {
    if (!token) {
      setContext(null);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`/api/auth/invites/validate?token=${encodeURIComponent(token)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;
        if (data?.ok && data?.valid && data?.invite?.email) {
          setContext({
            workspaceName: data.invite.workspaceName ?? '',
            email: data.invite.email,
            inviterName: data.invite.inviterName ?? '',
            role: data.invite.role ?? '',
            expiresAt: data.invite.expiresAt ?? '',
          });
        } else {
          setContext(null);
        }
      } catch {
        if (!cancelled) setContext(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return context;
}
